import { FunctionComponent, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
	faBullseye,
	faDatabase,
	faFlask,
	faSatelliteDish,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Spinner } from "@/components/Spinner/Spinner";
import { useSnackBar } from "@/utils/useSnackbar";
import { useLazyGetCompaniesQuery } from "@/pages/Private/redux/companies/companies.api";

import {
	useTriggerDeepResearchOnboardingMutation,
	useTriggerSignalConfigOnboardingMutation,
	useTriggerKnowledgeDbOnboardingMutation,
	useTriggerScoreOnboardingMutation,
} from "@/pages/Private/redux/feature/onboarding-trigger.api";

const CompanySelectionSchema = z.object({
	companyId: z.number().min(1, "Please select a company"),
});

type CompanySelection = z.infer<typeof CompanySelectionSchema>;

export const OnboardingTriggerTab: FunctionComponent = () => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [selectedCompanyName, setSelectedCompanyName] = useState("");
	const [triggerGetCompanies, { data: companiesData, isFetching: isCompaniesLoading }] =
		useLazyGetCompaniesQuery();

	const [triggerDeepResearch, { isLoading: isDeepResearchLoading }] =
		useTriggerDeepResearchOnboardingMutation();
	const [triggerSignalConfig, { isLoading: isSignalConfigLoading }] =
		useTriggerSignalConfigOnboardingMutation();
	const [triggerKnowledgeDb, { isLoading: isKnowledgeDbLoading }] =
		useTriggerKnowledgeDbOnboardingMutation();
	const [triggerScoreOnboarding, { isLoading: isScoreOnboardingLoading }] =
		useTriggerScoreOnboardingMutation();

	const { setValue, watch, reset } = useForm<CompanySelection>({
		defaultValues: { companyId: undefined as unknown as number },
		resolver: zodResolver(CompanySelectionSchema),
	});

	const companyId = watch("companyId");

	const companyOptions: AutoCompleteItem[] = useMemo(
		() =>
			companiesData?.data.map((company) => ({
				id: company.id,
				title: company.name,
			})) || [],
		[companiesData]
	);

	const handleDeepResearchTrigger = async () => {
		if (!companyId) {
			handleOpenSnackbar("Please select a company first");

			return;
		}

		try {
			await triggerDeepResearch(companyId).unwrap();
			handleOpenSnackbar("Deep research onboarding has been scheduled");
		} catch {
			handleOpenSnackbar("Failed to trigger deep research onboarding");
		}
	};

	const handleSignalConfigTrigger = async () => {
		if (!companyId) {
			handleOpenSnackbar("Please select a company first");

			return;
		}

		try {
			await triggerSignalConfig(companyId).unwrap();
			handleOpenSnackbar("Signal config onboarding has been scheduled");
		} catch {
			handleOpenSnackbar("Failed to trigger signal config onboarding");
		}
	};

	const handleKnowledgeDbTrigger = async () => {
		if (!companyId) {
			handleOpenSnackbar("Please select a company first");

			return;
		}

		try {
			await triggerKnowledgeDb(companyId).unwrap();
			handleOpenSnackbar("Knowledge DB onboarding has been scheduled");
		} catch {
			handleOpenSnackbar("Failed to trigger knowledge DB onboarding");
		}
	};

	const handleScoreOnboardingTrigger = async () => {
		if (!companyId) {
			handleOpenSnackbar("Please select a company first");

			return;
		}

		try {
			await triggerScoreOnboarding(companyId).unwrap();
			handleOpenSnackbar("Score onboarding has been retriggered");
		} catch {
			handleOpenSnackbar("Failed to retrigger score onboarding");
		}
	};

	const isAnyLoading =
		isDeepResearchLoading ||
		isSignalConfigLoading ||
		isKnowledgeDbLoading ||
		isScoreOnboardingLoading;

	return (
		<div className="flex flex-col gap-6">
			<div className="max-w-md">
				<label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
				<AutoComplete
					data={companyOptions}
					handleReset={() => {
						setSelectedCompanyName("");
						reset({ companyId: undefined as unknown as number });
					}}
					handleSearch={(value) => {
						triggerGetCompanies({ page: 1, limit: 20, searchValue: value });
					}}
					handleSelect={(item) => {
						if (item) {
							setValue("companyId", item.id as number);
							setSelectedCompanyName(item.title);
						}
					}}
					isLoading={isCompaniesLoading}
					placeholder="Search for a company..."
					selectedItem={selectedCompanyName}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
				<OnboardingCard
					description="Generates a deep research prompt template for the selected company. Processing happens asynchronously in the background."
					icon={<Icon icon={faFlask} />}
					isDisabled={!companyId || isAnyLoading}
					isLoading={isDeepResearchLoading}
					title="Deep Research Onboarding"
					onTrigger={handleDeepResearchTrigger}
				/>
				<OnboardingCard
					description="Generates the full signal configuration (prompts + priorities) for the selected company. Processing happens asynchronously in the background."
					icon={<Icon icon={faSatelliteDish} />}
					isDisabled={!companyId || isAnyLoading}
					isLoading={isSignalConfigLoading}
					title="Signal Config Onboarding"
					onTrigger={handleSignalConfigTrigger}
				/>
				<OnboardingCard
					description="Generates the full knowledge DB content (ICPs, products, case studies, objections) for the selected company. Processing happens asynchronously in the background."
					icon={<Icon icon={faDatabase} />}
					isDisabled={!companyId || isAnyLoading}
					isLoading={isKnowledgeDbLoading}
					title="Knowledge DB Onboarding"
					onTrigger={handleKnowledgeDbTrigger}
				/>
				<OnboardingCard
					description="Regenerates the score configuration (company categories, persona categories, scoring criteria) without triggering knowledge DB onboarding. Processing happens asynchronously in the background."
					icon={<Icon icon={faBullseye} />}
					isDisabled={!companyId || isAnyLoading}
					isLoading={isScoreOnboardingLoading}
					title="Score Onboarding"
					onTrigger={handleScoreOnboardingTrigger}
				/>
			</div>

			{renderMessageSnackbar({ vertical: "bottom", horizontal: "center" })}
		</div>
	);
};

interface OnboardingCardProps {
	title: string;
	description: string;
	icon: JSX.Element;
	isLoading: boolean;
	isDisabled: boolean;
	onTrigger: () => void;
}

const OnboardingCard: FunctionComponent<OnboardingCardProps> = ({
	title,
	description,
	icon,
	isLoading,
	isDisabled,
	onTrigger,
}) => {
	return (
		<div className="rounded-lg border border-gray-200 p-5 flex flex-col gap-4">
			<div className="flex items-center gap-3">
				<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-white text-brand-light">
					{icon}
				</div>
				<h3 className="text-sm font-semibold text-gray-900">{title}</h3>
			</div>
			<p className="text-sm text-gray-500">{description}</p>
			<div className="mt-auto">
				<Button
					color={ButtonColor.ACTION}
					disabled={isDisabled}
					image={isLoading ? <Spinner /> : undefined}
					isLoading={isLoading}
					size={ButtonSize.XS}
					title={isLoading ? "Processing..." : `Trigger ${title}`}
					onClick={onTrigger}
				/>
			</div>
		</div>
	);
};
